
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Header } from './Header';
import Footer from './Footer';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import Sidebar from './layout/Sidebar';
import { menuCategories } from './layout/menuData';
import { supabase } from '@/integrations/supabase/client';
import { AutoBreadcrumbs } from './navigation/AutoBreadcrumbs';
import { CommandPalette, useCommandPalette } from './navigation/CommandPalette';
import { TaskDrawerProvider } from '@/contexts/TaskDrawerContext';
import { FocusModeProvider, useFocusMode } from '@/contexts/FocusModeContext';
import { FocusModeToggle } from './navigation/FocusModeToggle';
import { OnboardingWizardV2 } from './onboarding/OnboardingWizardV2';
import { OnboardingChecklist } from './onboarding/OnboardingChecklist';
import { useOnboarding } from '@/hooks/useOnboarding';
import { SkipLink } from './ui/SkipLink';

// Lazy-load VALEN chat & smart alerts to keep them out of the initial bundle
const ValenSidebarChat = lazy(() =>
  import('./valen/ValenSidebarChat').then((m) => ({ default: m.ValenSidebarChat }))
);
const SmartAlertsLoader = lazy(() =>
  import('./SmartAlertsLoader').then((m) => ({ default: m.SmartAlertsLoader }))
);

type AppLayoutProps = {
  children: React.ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { compactSidebar } = useTheme();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [filteredMenuCategories, setFilteredMenuCategories] = useState(menuCategories);
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();

  useEffect(() => {
    const checkUserRole = async () => {
      const ownerEmail = 'vperez.mba@gmail.com';
      const isOwner = (user?.email ?? '').toLowerCase() === ownerEmail;

      if (!user) {
        setIsAdmin(false);
        setFilteredMenuCategories(
          menuCategories
            .filter(category => category.name !== 'Administración')
            .map(category => ({
              ...category,
              items: category.items.filter(item => item.path !== '/owner-executive')
            }))
        );
        return;
      }

      try {
        const { data: userIsAdmin, error } = await supabase
          .rpc('has_role', { _user_id: user.id, _role: 'admin' });

        if (error) {
          console.error('Error checking user role:', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(userIsAdmin || false);
        }

        const withoutAdmin = (userIsAdmin
          ? menuCategories
          : menuCategories.filter(category => category.name !== 'Administración'));

        const roleFiltered = withoutAdmin.map(category => ({
          ...category,
          items: category.items.filter(item => isOwner || item.path !== '/owner-executive')
        }));

        setFilteredMenuCategories(roleFiltered);
      } catch (error) {
        console.error('Error checking user role:', error);
        setIsAdmin(false);
        setFilteredMenuCategories(
          menuCategories
            .filter(category => category.name !== 'Administración')
            .map(category => ({
              ...category,
              items: category.items.filter(item => item.path !== '/owner-executive')
            }))
        );
      }
    };

    checkUserRole();
  }, [user]);

  const handleNavigation = (path: string) => {
    if (path === '/analytics?tab=intelligent') {
      navigate('/analytics');
      // Activar la pestaña intelligent después de navegar
      setTimeout(() => {
        const intelligentTab = document.querySelector('[value="intelligent"]') as HTMLElement;
        if (intelligentTab) {
          intelligentTab.click();
        }
      }, 100);
    } else {
      navigate(path);
    }
  };

  const isActive = (path: string) => {
    if (path === '/settings?tab=integrations') {
      return location.pathname === '/settings' && location.search === '?tab=integrations';
    }
    if (path === '/analytics?tab=intelligent') {
      return location.pathname === '/analytics';
    }
    return location.pathname === path;
  };

  // Don't show breadcrumbs on certain pages
  const showBreadcrumbs = location.pathname !== '/dashboard' && location.pathname !== '/';

  return (
    <FocusModeProvider>
      <TaskDrawerProvider>
        <AppLayoutShell
          user={user}
          compactSidebar={compactSidebar}
          filteredMenuCategories={filteredMenuCategories}
          handleNavigation={handleNavigation}
          isActive={isActive}
          showBreadcrumbs={showBreadcrumbs}
          paletteOpen={paletteOpen}
          setPaletteOpen={setPaletteOpen}
        >
          {children}
        </AppLayoutShell>
      </TaskDrawerProvider>
    </FocusModeProvider>
  );
};

interface ShellProps {
  user: ReturnType<typeof useAuth>['user'];
  compactSidebar: boolean;
  filteredMenuCategories: typeof menuCategories;
  handleNavigation: (path: string) => void;
  isActive: (path: string) => boolean;
  showBreadcrumbs: boolean;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  children: React.ReactNode;
}

const AppLayoutShell: React.FC<ShellProps> = ({
  user,
  compactSidebar,
  filteredMenuCategories,
  handleNavigation,
  isActive,
  showBreadcrumbs,
  paletteOpen,
  setPaletteOpen,
  children,
}) => {
  const { focusMode } = useFocusMode();
  const { shouldShowWizard } = useOnboarding();
  const [wizardOpen, setWizardOpen] = useState(false);

  // Autoabrir el wizard la primera vez que un usuario autenticado entra.
  useEffect(() => {
    if (user && shouldShowWizard) setWizardOpen(true);
  }, [user, shouldShowWizard]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SkipLink />
      {!focusMode && <Header />}

      <div className="flex flex-1 overflow-hidden">
        {!focusMode && (
          <Sidebar
            compactSidebar={compactSidebar}
            menuCategories={filteredMenuCategories}
            onNavigate={handleNavigation}
            isActive={isActive}
            onResumeOnboarding={() => setWizardOpen(true)}
          />
        )}

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto">
          <div className={focusMode ? 'mx-auto max-w-5xl px-6 py-6' : 'container mx-auto px-4 py-4'}>
            {!focusMode && showBreadcrumbs && <AutoBreadcrumbs />}
            <div className="flex justify-end pb-2">
              <FocusModeToggle showLabel />
            </div>
            <div className="py-2">{children}</div>
          </div>
        </main>
      </div>

      {!focusMode && <Footer />}

      {user && !focusMode && (
        <Suspense fallback={null}>
          <ValenSidebarChat />
        </Suspense>
      )}

      {user && (
        <Suspense fallback={null}>
          <SmartAlertsLoader />
        </Suspense>
      )}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      {user && <OnboardingWizardV2 open={wizardOpen} onOpenChange={setWizardOpen} />}
    </div>
  );
};

export default AppLayout;
